import { useState, useRef, useEffect } from 'react';
import { Download, Loader, ChevronDown } from 'lucide-react';
import { maquetaService } from '../services/api';

/**
 * BotonDescargarPdf
 * ─────────────────
 * Botón con menú desplegable que ofrece dos formatos:
 *   · Versión web    — PDF A4 con portada + índice + artículos completos
 *                      (no requiere que la edición tenga maqueta impresa).
 *   · Versión impresa — PDF tabloide 28×43 con la maqueta editorial
 *                      (solo si la edición tiene maqueta preparada).
 *
 * Muestra spinner y "Preparando…" durante la descarga, deshabilita el
 * botón, y mensaje claro si falla.
 *
 * Props:
 *   edition   { id, number } — datos mínimos de la edición
 *   variant   "primary" | "outline" — estilo del botón (default: outline)
 *   className — clase(s) adicional(es) para el botón principal
 */
export default function BotonDescargarPdf({ edition, variant = 'outline', className = '' }) {
  const [open, setOpen]         = useState(false);
  const [downloading, setDown]  = useState(null); // null | 'impresa' | 'web'
  const wrapperRef              = useRef(null);
  const buttonRef               = useRef(null);
  // Coordenadas del menú relativas al viewport (position: fixed). Se
  // recalculan al abrir para que quede pegado al botón y no lo corte
  // ningún ancestor con overflow:hidden.
  const [menuPos, setMenuPos]   = useState({ top: 0, left: 0, width: 260 });

  const openMenu = () => {
    if (buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect();
      const width = 260;
      // Alineado a la derecha del botón, con 6px de aire.
      const left = Math.min(r.right - width, window.innerWidth - width - 8);
      const top = r.bottom + 6;
      setMenuPos({ top, left: Math.max(8, left), width });
    }
    setOpen(true);
  };

  // Cerrar el menú al hacer clic fuera, al hacer scroll o resize
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const descargar = async (tipo) => {
    setOpen(false);
    setDown(tipo);
    try {
      const token = localStorage.getItem('newspaperToken');
      const url   = tipo === 'impresa'
        ? maquetaService.urlPdfPublico(edition.id)
        : maquetaService.urlPdfWebPublico(edition.id);
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        if (resp.status === 404) {
          throw new Error(tipo === 'impresa'
            ? 'Esta edición no tiene maqueta impresa todavía.'
            : 'Esta edición no está disponible para descarga.');
        }
        throw new Error('Error al descargar el PDF');
      }
      const blob = await resp.blob();
      const dl   = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = dl;
      a.download = `edicion-${edition.number}${tipo === 'web' ? '-web' : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(dl);
    } catch (err) {
      alert(err.message || 'Error al descargar el PDF');
    } finally {
      setDown(null);
    }
  };

  const btnClass = `btn btn-${variant} ${className}`.trim();

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        className={btnClass}
        onClick={() => !downloading && (open ? setOpen(false) : openMenu())}
        disabled={!!downloading}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {downloading ? (
          <>
            <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
            Preparando {downloading === 'web' ? 'versión web' : 'versión impresa'}…
          </>
        ) : (
          <>
            <Download size={16} />
            Descargar PDF
            <ChevronDown size={14} />
          </>
        )}
      </button>

      {open && !downloading && (
        <div style={{
          position: 'fixed',
          top: menuPos.top, left: menuPos.left, width: menuPos.width,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 10000,
          overflow: 'hidden',
        }}>
          <button
            onClick={() => descargar('web')}
            style={itemStyle}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f4f4f5'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Versión web</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              A4 · portada, índice y artículos completos
            </div>
          </button>
          <div style={{ height: 1, background: '#f4f4f5' }} />
          <button
            onClick={() => descargar('impresa')}
            style={itemStyle}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f4f4f5'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Versión impresa</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              Tabloide 28×43 cm · maqueta editorial
            </div>
          </button>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const itemStyle = {
  display: 'block', width: '100%', padding: '10px 14px',
  border: 'none', background: 'transparent', cursor: 'pointer',
  textAlign: 'left', transition: 'background .12s',
};
